import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp, ArrowDown, Minus, Check, X, Star, Globe } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import Speedometer from "../components/Speedometer";
import PillarCard from "../components/PillarCard";
import ScoreMovement from "../components/ScoreMovement";
import { StatusPill, Movement } from "../components/Indicators";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { STATUS, scoreColor } from "../lib/growth";
import useApi from "../hooks/useApi";
import { useBranch } from "../context/BranchContext";

const TREND = { 1: ArrowUp, "-1": ArrowDown, 0: Minus };

function SignalRow({ s }) {
  const st = STATUS[s.status] || STATUS.unavailable;
  const Tr = TREND[s.trend] || Minus;
  return (
    <div className="flex items-center gap-4 py-3 border-b last:border-0" style={{ borderColor: "#EDEEE9" }} data-testid={`signal-${s.key}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ink">{s.name}</span>
          <Tr size={12} strokeWidth={2.5} style={{ color: s.trend > 0 ? "#5F8213" : s.trend < 0 ? "#D95C5C" : "#C7C9C0" }} />
        </div>
        <p className="text-xs text-ink-2 mt-0.5 leading-relaxed">{s.evidence}</p>
      </div>
      <div className="hidden sm:block text-sm text-ink-2 w-32 shrink-0 text-right">{s.value}</div>
      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold shrink-0 w-32 justify-center" style={{ background: st.bg, color: st.color }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.dot }} />{st.label}
      </span>
      <div className="w-12 shrink-0 text-right font-bold text-sm tabular-nums" style={{ color: s.score == null ? "#C7C9C0" : "#16180F" }}>
        {s.score == null ? "—" : `${s.score}/10`}
      </div>
    </div>
  );
}

function ReputationStrip({ r }) {
  const items = [["Google rating", `${r.google_rating} ★`], ["Reviews", r.reviews], ["Last 30 days", `+${r.last_30}`], ["Response rate", `${r.response_rate}%`], ["Local rank", `#${r.local_rank}`]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4 p-4 rounded-xl" style={{ background: "#F7F8F2" }}>
      {items.map(([k, v]) => (
        <div key={k}><div className="text-lg font-extrabold tracking-tight text-ink">{v}</div><div className="text-[11px] text-ink-3">{k}</div></div>
      ))}
    </div>
  );
}

function WebsiteChecks({ checks }) {
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {checks.map((c) => (
        <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium" style={{ background: c.ok ? "rgba(147,192,32,.12)" : "rgba(217,92,92,.10)", color: c.ok ? "#5F8213" : "#C0455F" }}>
          {c.ok ? <Check size={12} strokeWidth={2.5} /> : <X size={12} strokeWidth={2.5} />}{c.label}
        </span>
      ))}
    </div>
  );
}

export default function GrowthScorePage() {
  const navigate = useNavigate();
  const { branchId } = useBranch();
  const [params] = useSearchParams();
  const openPillar = params.get("pillar");
  const { data } = useApi(`/growth/signals?branch_id=${branchId}`, [branchId]);

  if (!data) return <><TopBar /><div className="p-6 sm:p-8 max-w-[1400px]">
    <div className="mb-6 animate-rise">
      <h1 className="text-3xl font-extrabold tracking-tighter text-ink">Growth Score</h1>
      <p className="text-ink-2 mt-1">One score across your social, reputation, property marketing, website, local presence and brand.</p>
    </div>
    <div className="h-64 rounded-xl animate-pulse" style={{ background: "#EDEEE9" }} />
  </div></>;

  const moveMap = Object.fromEntries((data.movement || []).map((m) => [m.pillar, m.delta]));

  return (
    <>
      <TopBar />
      <div className="p-6 sm:p-8 max-w-[1400px]">
        <div className="mb-6 animate-rise">
          <h1 className="text-3xl font-extrabold tracking-tighter text-ink">Growth Score</h1>
          <p className="text-ink-2 mt-1">One score across your social, reputation, property marketing, website, local presence and brand.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 card p-8 flex flex-col items-center justify-center">
            <div className="rounded-3xl px-6 pt-5 pb-7 flex flex-col items-center" style={{ background: "linear-gradient(160deg,#1E2415 0%,#12160D 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.05), 0 24px 48px -28px rgba(0,0,0,.55)" }}>
              <Speedometer value={data.growth_score} delta={data.trend} size={280} dark />
              <div className="flex items-center gap-3 mt-6"><StatusPill status={data.status} dark /><Movement value={data.trend} className="text-sm" dark /></div>
            </div>
            <p className="text-sm text-ink-2 mt-3 text-center inline-flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#93C020" }} />
              Calculated from {data.active} active signals · updated continuously
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-4 content-start">
            {data.pillars.map((p) => <PillarCard key={p.key} pillar={p} movement={moveMap[p.key]} />)}
          </div>
        </div>

        <div className="mt-6"><ScoreMovement movement={data.movement} net={(data.movement || []).reduce((a, m) => a + m.delta, 0)} from={data.growth_score - data.trend} to={data.growth_score} /></div>

        <div className="mt-8 mb-4 flex items-end justify-between">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-ink">What Growth is measuring</h2>
            <p className="text-ink-2 text-sm mt-1">Every signal behind your score, with the evidence.</p>
          </div>
          <div className="text-right"><div className="text-2xl font-extrabold tracking-tighter text-ink">{data.active}</div><div className="label-caps">active signals</div></div>
        </div>

        <div className="card p-2 sm:p-4">
          <Accordion type="single" collapsible defaultValue={openPillar || data.groups[0]?.key}>
            {data.groups.map((g) => (
              <AccordionItem key={g.key} value={g.key} data-testid={`factor-group-${g.key}`}>
                <AccordionTrigger className="hover:no-underline px-2">
                  <div className="flex items-center gap-3 flex-1 text-left">
                    <span className="text-base font-bold text-ink">{g.label}</span>
                    <span className="label-caps">{g.weight}% of score</span>
                    <span className="ml-auto mr-3 text-sm font-bold tabular-nums" style={{ color: scoreColor(data.pillars.find((p) => p.key === g.key)?.score) }}>
                      {data.pillars.find((p) => p.key === g.key)?.score}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-2">
                  {g.key === "reputation" && <ReputationStrip r={data.summaries.reputation} />}
                  {g.key === "website_digital" && <WebsiteChecks checks={data.summaries.website_checks} />}
                  <div>{g.signals.map((s) => <SignalRow key={s.key} s={s} />)}</div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </>
  );
}
